
import { useEffect, useMemo, useState } from 'react';
import { FolderKanban, Library, Pencil, Plus, Trash2 } from 'lucide-react';
import { PainelConfirmDialog } from '@/components/painel/PainelFeedbackDialog';
import { PremiumCategoryCard } from '@/components/help-center/PremiumCategoryCard';
import { PainelPermissionsDialogShell } from '@/components/painel/permissions/painel-role-visuals';
import { IconLibraryPicker } from '@/components/painel/icon-library/IconLibraryPicker';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import type { CmsCategory as CmsCategoryType } from '@/lib/cms-types';
import {
  HELP_CENTER_HOME_CATEGORY_IDS,
  HOME_PAGE_CATEGORY_COLOR_CLASSES,
  mergeCategoriesWithHomeDefaults,
} from '@/lib/help-center-home-categories';
import { isLibraryIconRef, toLibraryIconRef } from '@/lib/icon-library';
import { cn } from '@/lib/utils';
import { CmsCategoryIcon, CMS_DEFAULT_ICON, CMS_ICON_OPTIONS } from './cms/cms-icons';
import { useCms } from './cms/cms-context';
import { painelInputClass, painelTextareaClass } from './cms/painel-form';
import {
  painelButtonOutlineClass,
  painelButtonPrimaryClass,
  painelModalCancelButtonClass,
} from '@/components/painel/painel-styles';
import { PainelRocketScroll } from '@/components/painel/PainelRocketScroll';
import { usePainelSaveFeedback } from '@/components/painel/PainelSaveFeedback';
import { usePainelText } from '@/components/painel/painel-texts-context';
import { EntitySeoSection } from '@/components/painel/seo/EntitySeoSection';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import { getGuideCategoryHref } from '@/lib/guide-routes';
import { sessionHasPermission } from '@/lib/painel-rbac';

const HOME_COLOR_OPTIONS = Object.keys(HOME_PAGE_CATEGORY_COLOR_CLASSES);

const HOME_COLOR_LABELS: Record<string, string> = {
  indigo: 'Índigo',
  blue: 'Azul',
  emerald: 'Esmeralda',
  amber: 'Âmbar',
  sky: 'Céu',
  violet: 'Violeta',
  rose: 'Rosa',
  cyan: 'Ciano',
  fuchsia: 'Fúcsia',
  teal: 'Teal',
  lime: 'Lima',
  green: 'Verde',
  orange: 'Laranja',
  yellow: 'Amarelo',
  red: 'Vermelho',
  pink: 'Pink',
  purple: 'Roxo',
  slate: 'Ardósia',
};

const painelCardActionBtnClass = cn(
  'h-8 rounded-lg border border-white/15 bg-[#030023]/85 px-2.5 font-poppins text-xs font-semibold text-blue-100 shadow-sm backdrop-blur-sm',
  'hover:border-sky-400/40 hover:bg-sky-500/15 hover:text-white'
);

type CategoryFormState = {
  name: string;
  description: string;
  icon: string;
  /** Texto livre no diálogo; vazio => sem badge ao guardar */
  badge: string;
  color: string;
};

function emptyForm(): CategoryFormState {
  return {
    name: '',
    description: '',
    icon: CMS_DEFAULT_ICON,
    badge: '',
    color: 'indigo',
  };
}

export function PainelCategoriesPage() {
  const pageText = usePainelText('categorias');
  const { showSaveSuccess } = usePainelSaveFeedback();
  const { session } = usePainelSession();
  const canManageSeo = sessionHasPermission(session, 'seo.manage');
  const canManageIcons = sessionHasPermission(session, 'icons.manage');
  const { categories, articles, hydrated, addCategory, updateCategory, deleteCategory } = useCms();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);

  const displayCategories = useMemo(
    () => mergeCategoriesWithHomeDefaults(categories),
    [categories]
  );

  const articleCountByCategory = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of articles) {
      m.set(a.categoryId, (m.get(a.categoryId) ?? 0) + 1);
    }
    return m;
  }, [articles]);

  useEffect(() => {
    if (!dialogOpen) {
      setEditingId(null);
      setForm(emptyForm());
    }
  }, [dialogOpen]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (c: CmsCategoryType) => {
    setEditingId(c.id);
    setForm({
      name: c.name,
      description: c.description,
      icon: c.icon,
      badge: c.badge != null && String(c.badge).trim() !== '' ? String(c.badge) : '',
      color:
        c.color && HOME_PAGE_CATEGORY_COLOR_CLASSES[c.color] ? c.color : 'indigo',
    });
    setDialogOpen(true);
  };

  const handleSave = () => {
    const name = form.name.trim();
    if (!name) return;
    const description = form.description.trim();
    const icon = form.icon || CMS_DEFAULT_ICON;
    const badgeTrim = form.badge.trim();
    const badge = badgeTrim === '' ? null : badgeTrim;
    const color =
      form.color && HOME_PAGE_CATEGORY_COLOR_CLASSES[form.color] ? form.color : 'indigo';
    if (editingId) {
      updateCategory(editingId, { name, description, icon, badge, color });
    } else {
      addCategory({ name, description, icon, badge, color });
    }
    setDialogOpen(false);
    showSaveSuccess({
      title: editingId ? 'Categoria atualizada' : 'Categoria criada',
      description: 'As alterações já estão reflectidas na homepage.',
    });
  };

  const confirmDelete = () => {
    if (deleteId) deleteCategory(deleteId);
    setDeleteId(null);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-poppins text-2xl font-bold tracking-tight text-white md:text-3xl">
            {pageText.title}
          </h1>
          <p className="mt-1 font-inter text-sm text-slate-400">{pageText.description}</p>
        </div>
        <Button
          type="button"
          onClick={openCreate}
          className={cn(
            'h-11 shrink-0 rounded-xl border border-sky-400/45 bg-sky-500/90 px-5 font-poppins text-sm font-semibold text-white shadow-lg shadow-sky-950/30',
            'transition-all hover:border-sky-300 hover:bg-sky-400 hover:shadow-sky-500/15'
          )}
        >
          <Plus className="mr-2 h-4 w-4 shrink-0" />
          Adicionar categoria
        </Button>
      </div>

      {!hydrated ? (
        <div className="rounded-2xl border border-white/10 bg-blue-950/35 px-6 py-16 text-center font-inter text-sm text-slate-400">
          Carregando…
        </div>
      ) : displayCategories.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-blue-950/30 px-6 py-16 text-center">
          <p className="font-poppins text-sm font-medium text-blue-100/85">
            Ainda não há categorias
          </p>
          <p className="mt-2 font-inter text-xs text-slate-400">
            Use &quot;Adicionar categoria&quot; para criar a primeira e ver a prévia aqui.
          </p>
          <Button
            type="button"
            onClick={openCreate}
            className={cn(
              'mt-6 rounded-xl border border-sky-400/45 bg-sky-500/90 font-poppins font-semibold text-white hover:bg-sky-400'
            )}
          >
            <Plus className="mr-2 h-4 w-4" />
            Adicionar categoria
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {displayCategories.map((c) => {
            const colorKey =
              c.color && HOME_PAGE_CATEGORY_COLOR_CLASSES[c.color] ? c.color : 'indigo';
            const colors = HOME_PAGE_CATEGORY_COLOR_CLASSES[colorKey];
            const badgeText =
              c.badge != null && String(c.badge).trim() !== ''
                ? String(c.badge).trim()
                : null;
            const count = articleCountByCategory.get(c.id) ?? 0;
            const isHomeLocked = HELP_CENTER_HOME_CATEGORY_IDS.has(c.id);

            return (
              <PremiumCategoryCard
                key={c.id}
                category={{
                  ...c,
                  badge: badgeText,
                  description:
                    c.description ||
                    'Sem descrição — edite o card para adicionar um resumo.',
                }}
                articleCount={count}
                iconClassName={colors.icon}
                badgeClassName={colors.badgeDark ?? 'bg-sky-500/20 text-sky-200'}
                badgeBorderColor={colors.badgeBorder ?? '#38bdf8'}
                hoverBorderClassName={colors.hoverBorder}
                hoverShadowClassName={colors.hoverShadow}
                onClick={() => openEdit(c)}
                actions={
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className={painelCardActionBtnClass}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(c);
                      }}
                    >
                      <Pencil className="mr-1.5 h-3.5 w-3.5" />
                      Editar
                    </Button>
                    {!isHomeLocked ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className={cn(
                          painelCardActionBtnClass,
                          'w-8 px-0 text-rose-300 hover:border-rose-400/40 hover:bg-rose-500/15 hover:text-rose-100'
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteId(c.id);
                        }}
                        aria-label="Excluir categoria"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    ) : null}
                  </>
                }
              />
            );
          })}
        </div>
      )}

      <p className="font-inter text-xs text-slate-400/70">
        Os dados são guardados localmente neste navegador. As categorias base da homepage não podem
        ser eliminadas — apenas editadas.
      </p>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          showCloseButton={false}
          className="max-h-[90vh] max-w-lg overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
        >
          <DialogTitle className="sr-only">
            {editingId ? 'Editar categoria' : 'Nova categoria'}
          </DialogTitle>
          <PainelPermissionsDialogShell>
            <div className="border-b border-white/10 bg-blue-950/20 px-6 pb-5 pt-6 sm:px-8">
              <div className="flex items-start gap-4 pr-14">
                <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500/25 to-sky-700/10 ring-1 ring-sky-400/35 shadow-[0_0_40px_-10px_rgba(56,189,248,0.35)]">
                  <FolderKanban className="h-7 w-7 text-sky-200" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/55">
                    Categorias
                  </p>
                  <h2 className="mt-1 font-poppins text-xl font-bold text-white sm:text-2xl">
                    {editingId ? 'Editar categoria' : 'Nova categoria'}
                  </h2>
                  <p className="mt-1.5 font-inter text-sm text-slate-400">
                    {editingId
                      ? 'Atualize o nome, descrição, badge, cor e ícone do card na homepage.'
                      : 'Defina o card que aparece na grelha de categorias da homepage.'}
                  </p>
                </div>
              </div>
            </div>

            <PainelRocketScroll
              className='max-h-[min(52vh,420px)] bg-[#030023]/30'
              viewportClassName='px-6 py-5 sm:px-8 scroll-smooth'
            >
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="cat-name" className="font-inter text-xs font-medium text-blue-100/90">
                    Nome
                  </Label>
                  <input
                    id="cat-name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className={painelInputClass}
                    placeholder="Ex.: Primeiros passos"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="cat-desc" className="font-inter text-xs font-medium text-blue-100/90">
                    Descrição
                  </Label>
                  <textarea
                    id="cat-desc"
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    className={cn(painelTextareaClass, 'min-h-[88px] resize-y')}
                    placeholder="Resumo curto da categoria"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="cat-badge" className="font-inter text-xs font-medium text-blue-100/90">
                    Badge (opcional)
                  </Label>
                  <input
                    id="cat-badge"
                    value={form.badge}
                    onChange={(e) => setForm((f) => ({ ...f, badge: e.target.value }))}
                    className={painelInputClass}
                    placeholder="Ex.: Novo, Popular — vazio = sem etiqueta"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="font-inter text-xs font-medium text-blue-100/90">Cor do tema</Label>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                    {HOME_COLOR_OPTIONS.map((key) => {
                      const colors = HOME_PAGE_CATEGORY_COLOR_CLASSES[key];
                      const selected = (form.color ?? 'indigo') === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, color: key }))}
                          className={cn(
                            'flex flex-col items-center gap-1.5 rounded-xl border px-2 py-2.5 transition-all',
                            selected
                              ? 'border-sky-400/50 bg-sky-500/15 ring-1 ring-sky-400/30'
                              : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.06]'
                          )}
                          aria-pressed={selected}
                          title={HOME_COLOR_LABELS[key] ?? key}
                        >
                          <span
                            className="h-7 w-7 rounded-full shadow-inner ring-2 ring-white/15"
                            style={{ backgroundColor: colors?.badgeBorder ?? '#38bdf8' }}
                            aria-hidden
                          />
                          <span className="font-inter text-[10px] font-medium text-blue-100/80">
                            {HOME_COLOR_LABELS[key] ?? key}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid gap-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label className="font-inter text-xs font-medium text-blue-100/90">Ícone</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={cn(painelButtonOutlineClass, 'h-8 gap-1.5 px-2.5 text-xs')}
                      onClick={() => setLibraryPickerOpen(true)}
                    >
                      <Library className="h-3.5 w-3.5" />
                      Da biblioteca
                    </Button>
                  </div>
                  {isLibraryIconRef(form.icon) ? (
                    <div className="flex items-center gap-3 rounded-xl border border-sky-400/25 bg-sky-500/[0.08] px-3 py-2">
                      <CmsCategoryIcon name={form.icon} className="h-6 w-6 text-sky-200" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-mono text-xs text-sky-200/80">{form.icon}</p>
                        <p className="font-inter text-[10px] text-slate-500">Icon Library</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs text-slate-400 hover:text-white"
                        onClick={() => setForm((f) => ({ ...f, icon: CMS_DEFAULT_ICON }))}
                      >
                        Usar Lucide
                      </Button>
                    </div>
                  ) : null}
                  <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-8">
                    {CMS_ICON_OPTIONS.map((opt) => {
                      const selected = form.icon === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, icon: opt.value }))}
                          className={cn(
                            'inline-flex h-10 w-full items-center justify-center rounded-xl border transition-all',
                            selected
                              ? 'border-sky-400/50 bg-sky-500/15 text-sky-100 ring-1 ring-sky-400/30'
                              : 'border-white/10 bg-white/[0.03] text-blue-100/75 hover:border-white/20 hover:bg-white/[0.06] hover:text-white'
                          )}
                          aria-pressed={selected}
                          aria-label={opt.label}
                          title={opt.label}
                        >
                          <CmsCategoryIcon name={opt.value} className="h-[18px] w-[18px]" />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {editingId && /^\d+$/.test(editingId) && canManageSeo ? (
                  <div className="space-y-2 border-t border-white/10 pt-4">
                    <p className="font-poppins text-xs font-semibold uppercase tracking-wide text-slate-400">
                      SEO da categoria
                    </p>
                    <EntitySeoSection
                      entityType="category"
                      entityId={Number(editingId)}
                      enabled
                      fallbackTitle={form.name}
                      fallbackDescription={form.description}
                      previewPath={getGuideCategoryHref(editingId)}
                    />
                  </div>
                ) : null}
              </div>
            </PainelRocketScroll>

            <div className="flex flex-col-reverse gap-2 border-t border-white/10 bg-blue-950/25 px-6 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-8">
              <Button
                type="button"
                variant="ghost"
                className={painelModalCancelButtonClass}
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                className={painelButtonPrimaryClass}
                onClick={handleSave}
                disabled={!form.name.trim()}
              >
                Salvar
              </Button>
            </div>
          </PainelPermissionsDialogShell>
        </DialogContent>
      </Dialog>

      <PainelConfirmDialog
        open={deleteId !== null}
        onOpenChange={(o) => !o && setDeleteId(null)}
        title="Excluir categoria?"
        description="Os guias desta categoria também serão removidos da biblioteca do painel."
        confirmLabel="Excluir"
        onConfirm={confirmDelete}
      />

      <IconLibraryPicker
        open={libraryPickerOpen}
        onOpenChange={setLibraryPickerOpen}
        auth={canManageIcons}
        onSelect={(item) => setForm((f) => ({ ...f, icon: toLibraryIconRef(item.slug) }))}
      />
    </div>
  );
}
